import React, { FC, useMemo, useEffect } from "react";
import { Alert, Divider, Select } from "antd";
import Title from "antd/es/typography/Title";
import { useFormikContext } from "formik";
import { useTranslation } from "react-i18next";

import { useGetAllPackagesQuery, PackagingItem } from "shared/api";
import { adaptPackagesForMultipleExecuteReport } from "shared/adapters";
import { ExecuteReportValues, AdaptedValues } from "shared/types";
import { Colors } from "shared/themes";
import { FormSelect, Loader } from "shared/ui";

interface MultiOrdersPackagesFilterProps {
  adaptedOrders: AdaptedValues[];
  isLoadingOrders: boolean;
  onOrderSearch: (value: string) => void;
}

const MultiOrdersPackagesFilter: FC<MultiOrdersPackagesFilterProps> = ({
  adaptedOrders,
  isLoadingOrders,
  onOrderSearch,
}) => {
  const { t } = useTranslation("", { keyPrefix: "executeReports" });

  const { values, setFieldValue, handleBlur } =
    useFormikContext<ExecuteReportValues>();

  const orderIds = values.order_ids ?? [];

  useEffect(() => {
    setFieldValue("packaging_ids", []);
  }, [orderIds]);

  const { data: packagings, isLoading } = useGetAllPackagesQuery(
    { order__in: orderIds, expand: "material,order" },
    { skip: !orderIds.length }
  );

  const adaptedPackages = useMemo(
    () =>
      adaptPackagesForMultipleExecuteReport(
        packagings as unknown as PackagingItem[]
      ),
    [packagings]
  );

  if (isLoading) {
    return <Loader type="fullscreen-backdrop" />;
  }

  return (
    <>
      <Divider className="my-1.5" style={{ background: Colors.blue.primary }} />

      <div className="flex gap-x-3">
        <div className="flex flex-col gap-y-0.5">
          <div className="flex gap-x-2">
            <Title level={5} className="whitespace-nowrap !m-0">
              {t("selectOrder")}
            </Title>

            {" - "}

            <div className="w-[260px]">
              <Select
                mode="multiple"
                value={orderIds}
                placeholder={t("selectOrder")}
                options={adaptedOrders}
                loading={isLoadingOrders}
                onChange={(value: number[]) =>
                  setFieldValue("order_ids", value)
                }
                onSearch={onOrderSearch}
                showSearch
                filterOption={false}
                size="small"
                className="w-full"
              />
            </div>
          </div>
          <span className="text-[10px] leading-tight text-gray-400">
            * {t("orderSearchHint")}
          </span>
        </div>

        {!!orderIds.length && (
          <>
            <Divider type="vertical" />

            <div className="flex gap-x-2">
              <Title level={5} className="!m-0">
                {t("packages")}
              </Title>

              {adaptedPackages.length ? (
                <div className="w-[420px]">
                  <FormSelect
                    multiple
                    value={values.packaging_ids}
                    fieldName="packaging_ids"
                    options={adaptedPackages}
                    setFieldValue={setFieldValue}
                    handleBlur={handleBlur}
                    placeholder={t("packages")}
                    size="small"
                    className="w-full"
                  />
                </div>
              ) : (
                <Alert type="warning" message={t("noOptions")} />
              )}
            </div>
          </>
        )}
      </div>

      <Divider className="my-1.5" style={{ background: Colors.blue.primary }} />
    </>
  );
};

export { MultiOrdersPackagesFilter };
